import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  collectionGroup,
  onSnapshot,
  orderBy,
  query,
} from "firebase/firestore";

import { dbFinancialPromotions } from "../../../../backend/firebaseconfig";
import { useUserEmail } from "../../../../context/UserEmailContext";
import { useUserRole } from "../../../../context/UserRoleContext";
import "../../../../css/FinancialPromotions/Dashboard/Dashboard.css";
import "../../../../css/FinancialPromotions/Dashboard/SubmissionRow.css";
import { useLocation, useNavigate } from "react-router-dom";
import Banner from "../../AML/Components/Banners/Banner";
import FPPrimaryButton from "../components/buttons/PrimaryButton";
import Checkbox from "../components/buttons/Checkbox";
import SearchField from "../components/buttons/SearchField";
import { ReactComponent as FilterIcon } from "../../../../assets/icons/financialPromotions/Filter.svg";
import Breadcrumb from "../../../DesignSystem/Breadcrumb/Breadcrumb";

import SubmissionRow from "./helperFunctions/SubmissionRow.jsx"; // if extracted
import { ReactComponent as NewSubmissionIcon } from "../../../../assets/icons/financialPromotions/new-submission.svg"; // if you want a custom icon for the "New Submission" button
import { useUserPermissions } from "../../../../context/UserPermissionsContext.js";
import { useAdminAdviserMails } from "../../../../context/AdminAdviserEmailsContext.js";
import { getRelatedEmails } from "../../AML/Utils/getRelatedEmails";
import getAllAdvisers from "../../../../context/HelperFunctions/getAllAdvisers";
import CustomSelect from "../../../DesignSystem/Dropdowns/CustomSelect";
// or if you kept it in same file, no import needed

// const PAGE_SIZE = 50;
const FINANCIAL_PROMOTIONS_ADMIN_PERMISSIONS = new Set([
  "financial promotions admin",
  "financail promotions admin",
  "financial_promotions_admin",
]);

// Firestore structure
const ROOT_COLLECTION = "Approvals";
const SUBMISSIONS_SUBCOL = "Submissions";

export default function UserApprovals() {
  const { userEmail } = useUserEmail();
  const { userRole } = useUserRole();
  const { userPermissions } = useUserPermissions();
  const location = useLocation();
  const navigate = useNavigate();

  const { adminAdviserMails } = useAdminAdviserMails();
  const [relatedEmails, setRelatedEmails] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [selected, setSelected] = useState(null); // { ref, data, submissionId }
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [isFilterReversed, setIsFilterReversed] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [staffSelectedAdviserEmail, setStaffSelectedAdviserEmail] =
    useState("");
  const [adviserOptions, setAdviserOptions] = useState([]);
  const [loadingAdvisers, setLoadingAdvisers] = useState(false);

  const normalizedUserRole = String(userRole || "")
    .trim()
    .toLowerCase();
  const hasFinancialPromotionsAdminPermission = Array.isArray(userPermissions)
    ? userPermissions.some((permission) =>
        FINANCIAL_PROMOTIONS_ADMIN_PERMISSIONS.has(
          String(permission || "")
            .trim()
            .toLowerCase(),
        ),
      )
    : false;
  const isAdminView =
    normalizedUserRole === "staff" && hasFinancialPromotionsAdminPermission;
  const isStaff = normalizedUserRole === "staff";

  const breadcrumbItems = [
    { label: "Marketing & Promotions", to: "/portal/marketing-promotions" },
    { label: "Financial Promotions" },
  ];

  useEffect(() => {
    if (!location.state?.justSubmitted) return;

    setSuccessMessage(
      "Your financial promotion has been submitted successfully and is now awaiting review.",
    );

    // Clear route state so the banner only appears once.
    navigate(location.pathname, { replace: true });
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    if (!successMessage) return undefined;

    const timeoutId = window.setTimeout(() => {
      setSuccessMessage("");
    }, 5000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [successMessage]);

  useEffect(() => {
    if (!isStaff) {
      setStaffSelectedAdviserEmail("");
      setAdviserOptions([]);
      return undefined;
    }

    let cancelled = false;

    const loadAdvisers = async () => {
      try {
        setLoadingAdvisers(true);
        const advisers = await getAllAdvisers();
        if (cancelled) return;

        const optionsByEmail = new Map();
        advisers.forEach((adviser) => {
          const email = String(adviser?.email || adviser?.userId || "")
            .trim()
            .toLowerCase();
          if (!email) return;

          optionsByEmail.set(email, {
            id: email,
            name: email,
          });
        });

        setAdviserOptions(
          Array.from(optionsByEmail.values()).sort((a, b) =>
            a.name.localeCompare(b.name),
          ),
        );
      } catch (error) {
        console.error("[FP Dashboard] Failed to load advisers:", error);
        if (!cancelled) setAdviserOptions([]);
      } finally {
        if (!cancelled) setLoadingAdvisers(false);
      }
    };

    loadAdvisers();
    return () => {
      cancelled = true;
    };
  }, [isStaff]);

  // ------------------------
  // Build related emails list (admin sees their advisers too)
  // ------------------------
  useEffect(() => {
    if (!userEmail || !userRole) return;

    let cancelled = false;

    const normalise = (e) => (e || "").trim().toLowerCase();

    const fetchEmails = async () => {
      const emailSet = new Set();
      emailSet.add(normalise(userEmail));

      if (Array.isArray(adminAdviserMails)) {
        adminAdviserMails.forEach((e) => {
          const clean = normalise(e);
          if (clean) emailSet.add(clean);
        });
      }

      try {
        const emails = await getRelatedEmails(userEmail, userRole);
        emails.forEach((e) => {
          const clean = normalise(e);
          if (clean) emailSet.add(clean);
        });
      } catch (err) {
        console.error("[FP Dashboard] Failed to fetch related emails:", err);
      }

      if (!cancelled) setRelatedEmails(Array.from(emailSet));
    };

    fetchEmails();
    return () => {
      cancelled = true;
    };
  }, [userEmail, userRole, adminAdviserMails]);

  // ------------------------
  // Load submissions
  // ------------------------
  useEffect(() => {
    if (!dbFinancialPromotions) return undefined;
    if (!userEmail) return undefined;
    if (relatedEmails.length === 0) return undefined;

    if (isStaff) {
      // Staff see everything, then can switch to an individual adviser's view.
      const q = query(
        collectionGroup(dbFinancialPromotions, SUBMISSIONS_SUBCOL),
        orderBy("createdAt", "desc"),
      );

      const unsub = onSnapshot(
        q,
        (snap) => {
          const rows = snap.docs.map((d) => ({
            id: d.id,
            ref: d.ref,
            ownerEmail:
              d.ref.parent?.parent?.id || d.data()?.createdByEmail || "",
            ...d.data(),
          }));
          setSubmissions(rows);
        },
        (err) => console.error("Submission snapshot error:", err),
      );

      return () => unsub();
    }

    // Advisers and Administrators: query each related email's path
    const unsubscribers = [];
    const docsBySource = {};

    const publishSubmissions = () => {
      const uniqueRows = new Map();

      Object.values(docsBySource)
        .flat()
        .forEach((row) => {
          const key = row?.ref?.path || `${row?.ownerEmail}/${row?.id}`;
          uniqueRows.set(key, row);
        });

      setSubmissions(Array.from(uniqueRows.values()));
    };

    const mapSubmission = (submissionDoc, fallbackEmail = "") => ({
      id: submissionDoc.id,
      ref: submissionDoc.ref,
      ownerEmail:
        submissionDoc.ref.parent?.parent?.id ||
        submissionDoc.data()?.createdByEmail ||
        fallbackEmail,
      ...submissionDoc.data(),
    });

    relatedEmails.forEach((email) => {
      const q = query(
        collection(
          dbFinancialPromotions,
          ROOT_COLLECTION,
          email,
          SUBMISSIONS_SUBCOL,
        ),
        orderBy("createdAt", "desc"),
      );

      const unsub = onSnapshot(
        q,
        (snap) => {
          docsBySource[`owner:${email}`] = snap.docs.map((submissionDoc) =>
            mapSubmission(submissionDoc, email),
          );
          publishSubmissions();
        },
        (err) =>
          console.error(`[FP Dashboard] Snapshot error for ${email}:`, err),
      );

      unsubscribers.push(unsub);
    });

    // The Staff Portal stores reassignment on the submission itself. Include
    // staff-owned submissions assigned to the currently signed-in adviser.
    if (normalizedUserRole === "advisers") {
      const adviserEmail = String(userEmail || "").trim().toLowerCase();
      const assignedQuery = query(
        collectionGroup(dbFinancialPromotions, SUBMISSIONS_SUBCOL),
        orderBy("createdAt", "desc"),
      );

      const unsub = onSnapshot(
        assignedQuery,
        (snap) => {
          docsBySource.assigned = snap.docs
            .filter((submissionDoc) => {
              const submissionData = submissionDoc.data() || {};
              const assignedEmail = String(
                submissionData.assignedAdviserEmail ||
                  submissionData.assignedAdviser?.email ||
                  "",
              )
                .trim()
                .toLowerCase();

              return assignedEmail === adviserEmail;
            })
            .map((submissionDoc) => mapSubmission(submissionDoc));
          publishSubmissions();
        },
        (err) =>
          console.error("[FP Dashboard] Assigned adviser lookup failed:", err),
      );

      unsubscribers.push(unsub);
    }

    return () => unsubscribers.forEach((unsub) => unsub && unsub());
  }, [isStaff, normalizedUserRole, userEmail, relatedEmails]);

  const normalizeStatus = (status) =>
    String(status || "")
      .trim()
      .toLowerCase();

  const statusMatchesFilter = (status, filter) => {
    if (filter === "all") return true;

    const normalized = normalizeStatus(status);

    if (filter === "approved") {
      return ["approved", "completed"].includes(normalized);
    }

    if (filter === "action_required") {
      return [
        "action_required",
        "requested_changes",
        "changes_requested",
        "needs_action",
      ].includes(normalized);
    }

    return normalized === filter;
  };

  const filteredSubmissions = useMemo(() => {
    const queryText = String(searchTerm || "")
      .trim()
      .toLowerCase();

    const toEpoch = (value) => {
      if (!value) return 0;
      if (typeof value?.toDate === "function") {
        const date = value.toDate();
        return Number.isNaN(date?.getTime?.()) ? 0 : date.getTime();
      }
      if (typeof value?.seconds === "number") {
        return value.seconds * 1000;
      }
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? 0 : date.getTime();
    };

    const getStatusPriority = (status) => {
      const normalized = normalizeStatus(status);

      if (
        [
          "action_required",
          "requested_changes",
          "needs_action",
          "changes_requested",
          "referred",
        ].includes(normalized)
      ) {
        return 0;
      }

      if (
        [
          "awaiting_response",
          "awaiting_review",
          "awaiting",
          "submitted",
          "new",
          "in_review",
          "in review",
          "processing",
        ].includes(normalized)
      ) {
        return 1;
      }

      if (
        ["approved", "completed", "declined", "rejected", "failed"].includes(
          normalized,
        )
      ) {
        return 2;
      }

      return 3;
    };

    const matched = submissions.filter((submission) => {
      if (isStaff && staffSelectedAdviserEmail) {
        const selectedEmail = staffSelectedAdviserEmail.trim().toLowerCase();
        const ownerEmail = String(submission?.ownerEmail || "")
          .trim()
          .toLowerCase();
        const assignedEmail = String(
          submission?.assignedAdviserEmail ||
            submission?.assignedAdviser?.email ||
            "",
        )
          .trim()
          .toLowerCase();

        if (ownerEmail !== selectedEmail && assignedEmail !== selectedEmail) {
          return false;
        }
      }

      if (!statusMatchesFilter(submission?.status, statusFilter)) {
        return false;
      }

      if (!queryText) return true;

      const searchableText = [
        submission?.title,
        submission?.contentType,
        submission?.reference,
        submission?.referenceNumber,
        submission?.submissionReference,
        submission?.id,
        submission?.status,
        submission?.ownerEmail,
        submission?.createdByEmail,
        submission?.assignedAdviserEmail,
        submission?.assignedAdviser?.email,
        submission?.assignedAdviserName,
        Array.isArray(submission?.mediaTypes)
          ? submission.mediaTypes.join(" ")
          : submission?.mediaTypes,
        Array.isArray(submission?.productTypes)
          ? submission.productTypes.join(" ")
          : submission?.productTypes,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchableText.includes(queryText);
    });

    return matched.sort((a, b) => {
      if (statusFilter === "all") {
        const rankDiff = isFilterReversed
          ? getStatusPriority(b?.status) - getStatusPriority(a?.status)
          : getStatusPriority(a?.status) - getStatusPriority(b?.status);
        if (rankDiff !== 0) return rankDiff;
      }

      const bTime = toEpoch(b?.updatedAt) || toEpoch(b?.createdAt);
      const aTime = toEpoch(a?.updatedAt) || toEpoch(a?.createdAt);
      const timeDiff = isFilterReversed ? aTime - bTime : bTime - aTime;
      if (timeDiff !== 0) return timeDiff;

      return String(b?.id || "").localeCompare(String(a?.id || ""));
    });
  }, [
    submissions,
    statusFilter,
    searchTerm,
    isFilterReversed,
    isStaff,
    staffSelectedAdviserEmail,
  ]);

  // ------------------------
  // UI
  // ------------------------
  // console.log("submission",submissions[0]);
  return (
    // inside UserApprovals() return, replace your current content body with:

    <div className="fp-dashboard">
      <Breadcrumb items={breadcrumbItems} />

      {/* Header */}
      <div className="fp-dashboard-header">
        <div className="FP_H3">Financial Promotions</div>
        <div className="fp-dashboard-header-actions">
          {isStaff && (
            <div className="fp-dashboard-adviser-selector">
              <CustomSelect
                label="View submissions for adviser"
                options={adviserOptions}
                value={staffSelectedAdviserEmail}
                onChange={(event) => {
                  setStaffSelectedAdviserEmail(event.target.value);
                  setSearchTerm("");
                  setStatusFilter("all");
                }}
                placeholder={
                  loadingAdvisers
                    ? "Loading adviser emails..."
                    : "All adviser submissions"
                }
                searchPlaceholder="Search adviser email..."
                disabled={loadingAdvisers}
                clearable
                searchable
              />
            </div>
          )}
          <FPPrimaryButton
            onClick={() =>
              navigate("/financial-promotions/new", { state: { mode: "new" } })
            }
            icon={<NewSubmissionIcon />}
          >
            New Submission
          </FPPrimaryButton>
        </div>
      </div>

      {successMessage && <Banner type="success" message={successMessage} />}

      {/* Filters */}
      <div className="fp-dashboard-toolbar">
        <div className="fp-dashboard-header">
          <div className="fp-dashboard-content-header fp-dashboard-content-header--filters">
            <Checkbox
              label="Approved"
              checked={statusFilter === "approved"}
              onChange={(e) =>
                setStatusFilter(e.target.checked ? "approved" : "all")
              }
            />
            <Checkbox
              label="Action Required"
              checked={statusFilter === "action_required"}
              onChange={(e) =>
                setStatusFilter(e.target.checked ? "action_required" : "all")
              }
            />
          </div>

          <div className="fp-dashboard-content-header fp-dashboard-content-header--search">
            <SearchField
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search..."
            />
            <FilterIcon
              className="fp-filter-icon"
              role="button"
              tabIndex={0}
              onClick={() => setIsFilterReversed((prev) => !prev)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  setIsFilterReversed((prev) => !prev);
                }
              }}
            />
          </div>
        </div>
        {/* List card */}
        <div className="fp-submissions-card">
          {filteredSubmissions.length === 0 ? (
            <div className="FP_Help_Text">No submissions found.</div>
          ) : (
            filteredSubmissions.map((s) => (
              <SubmissionRow
                key={s.id}
                submission={s}
                isAdminView={isAdminView}
                isActive={selected?.submissionId === s.id}
                onClick={() =>
                  // setSelected({ ref: s.ref, data: s, submissionId: s.id })
                  navigate(`/financial-promotions/details/${s.id}`, {
                    state: {
                      submissionId: s.id,
                      ownerEmail:
                        s.ownerEmail || s.createdByEmail || userEmail || "",
                    },
                  })
                }
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}
